import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import PhygitalIcon from "@/components/PhygitalIcon";
import AdvocatePortalPage from "@/pages/AdvocatePortalPage";
import {
  Users,
  TrendingUp,
  Globe,
  ArrowLeft,
  Loader2,
  Bell,
  Filter,
  ExternalLink,
  FileText,
  HeartHandshake,
  RefreshCw,
  Tag
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
};

const PORTAL_META = {
  team: { name: "Team", icon: Users, accent: "blue", accentBg: "bg-blue-100", accentText: "text-blue-700" },
  investor: { name: "Investor", icon: TrendingUp, accent: "emerald", accentBg: "bg-emerald-100", accentText: "text-emerald-700" },
  network: { name: "Network", icon: Globe, accent: "purple", accentBg: "bg-purple-100", accentText: "text-purple-700" },
};

// Shared Updates component
const PortalUpdates = ({ portalFilter, accentColor = "blue" }) => {
  const [updates, setUpdates] = useState([]);
  const [filterTag, setFilterTag] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/admin/updates`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          const all = data.updates || [];
          setUpdates(all.filter(u =>
            u.status === "published" &&
            (!portalFilter || (u.assigned_portals || []).includes(portalFilter) || (u.assigned_portals || []).length === 0)
          ));
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [portalFilter]);

  const allTags = [...new Set(updates.flatMap(u => u.tags || []))];
  const visible = filterTag ? updates.filter(u => (u.tags || []).includes(filterTag)) : updates;

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Bell className={`w-4 h-4 text-${accentColor}-500`} /> Updates
        </h3>
        {allTags.length > 0 && (
          <div className="flex items-center gap-1.5">
            <Filter className="w-3 h-3 text-gray-400" />
            <button onClick={() => setFilterTag("")} className={`text-xs px-2 py-0.5 rounded-full ${!filterTag ? `bg-${accentColor}-100 text-${accentColor}-700` : 'bg-gray-100 text-gray-500'}`}>All</button>
            {allTags.map(tag => (
              <button key={tag} onClick={() => setFilterTag(tag)} className={`text-xs px-2 py-0.5 rounded-full ${filterTag === tag ? `bg-${accentColor}-100 text-${accentColor}-700` : 'bg-gray-100 text-gray-500'}`}>{tag}</button>
            ))}
          </div>
        )}
      </div>
      {visible.length > 0 ? (
        <div className="space-y-4">
          {visible.map((update) => (
            <Card key={update.update_id} className="border-gray-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`update-card-${update.update_id}`}>
              {update.cover_image && (
                <div className="w-full" style={{ aspectRatio: '16/9' }}>
                  <img src={update.cover_image.startsWith('http') ? update.cover_image : `${API}${update.cover_image}`} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-800 text-sm">{update.title}</h4>
                    {update.body && <p className="text-xs text-gray-500 mt-1 line-clamp-3">{update.body}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {(update.tags || []).map(tag => (
                      <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0">{tag}</Badge>
                    ))}
                  </div>
                </div>
                {update.created_at && (
                  <p className="text-[10px] text-gray-400 mt-2">{new Date(update.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="py-10 text-center text-sm text-gray-400">No updates yet</CardContent></Card>
      )}
    </div>
  );
};

// Investor-specific: projection simulator + full portal link
const InvestorContent = ({ portalId }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border-emerald-200 bg-emerald-50/50 cursor-pointer hover:shadow-md transition-all" onClick={() => portalId && navigate(`/investor/portal/${portalId}`)} data-testid="investor-full-portal-link">
          <CardContent className="p-6 text-center">
            <TrendingUp className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-800 mb-1">Full Investor Portal</h3>
            <p className="text-sm text-gray-500">Metrics, pitch deck, roadmap & overview</p>
            <Button variant="outline" className="mt-4 border-emerald-300 text-emerald-700 hover:bg-emerald-100" data-testid="open-investor-portal-btn">
              Open Portal <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50 cursor-pointer hover:shadow-md transition-all" onClick={() => navigate('/admin/financials')} data-testid="projection-simulator-link">
          <CardContent className="p-6 text-center">
            <FileText className="w-10 h-10 text-blue-600 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-800 mb-1">Live Projections</h3>
            <p className="text-sm text-gray-500">View & edit the financial model</p>
            <Button variant="outline" className="mt-4 border-blue-300 text-blue-700 hover:bg-blue-100">
              Open Projections <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
      <PortalUpdates portalFilter="investor" accentColor="emerald" />
    </div>
  );
};

export default function SupportCategoryPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [investorPortalId, setInvestorPortalId] = useState(null);
  const [isAdvocate, setIsAdvocate] = useState(false);
  const [loading, setLoading] = useState(true);

  const meta = PORTAL_META[category] || PORTAL_META.team;
  const Icon = meta.icon;

  useEffect(() => {
    const load = async () => {
      try {
        const [meRes, advRes] = await Promise.all([
          fetch(`${API}/api/auth/me`, { headers: getHeaders() }),
          fetch(`${API}/api/user/advocate-status`, { headers: getHeaders() }),
        ]);
        if (meRes.ok) setUser(await meRes.json());
        else { navigate("/login"); return; }
        if (advRes.ok) { const d = await advRes.json(); setIsAdvocate(d.is_advocate || false); }

        if (category === "investor") {
          const invRes = await fetch(`${API}/api/user/my-investor-portal`, { headers: getHeaders() });
          if (invRes.ok) {
            const d = await invRes.json();
            if (d.portal_id) setInvestorPortalId(d.portal_id);
          }
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [category, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid={`support-${category}-page`}>
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/support" className="text-gray-400 hover:text-black transition-colors" data-testid="support-cat-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className={`w-9 h-9 rounded-lg ${meta.accentBg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${meta.accentText}`} />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{meta.name}</h1>
            <p className="text-xs text-gray-400">
              {user?.first_name || user?.name || "Member"}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {/* Investor-specific content */}
        {category === "investor" && <InvestorContent portalId={investorPortalId} />}

        {/* Team/Network content */}
        {(category === "team" || category === "network") && (
          <>
            <PortalUpdates portalFilter={category} accentColor={meta.accent} />
            {isAdvocate && (
              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <HeartHandshake className="w-4 h-4 text-teal-500" /> Advocate Dashboard
                </h3>
                <AdvocatePortalPage embedded />
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
